import type { ComboboxItem } from '@/components'
import { Button, Checkbox, Combobox, Input, UserCombobox } from '@/components'
import { ExportFormat, ExportOption, ExportType } from '@/modules/exports'
import type { ExportPeriod } from '@/modules/exports/components/export-form'
import {
  ExportCard,
  ExportField,
  ExportFormatField,
  ExportMethodField,
  ExportPeriodField,
} from '@/modules/exports/components/export-form'
import { useMassIssueExportForm } from '@/modules/issues'
import { USERS_COMBOBOX_QUERY } from '@/modules/users/queries'
import { emailValidation } from '@/utils/forms'
import { useUser } from '@/utils/hooks'
import { useQuery } from '@apollo/client'
import { useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { endOfDay, startOfDay } from 'date-fns'
import { Plus, X } from 'lucide-react'
import type { KeyboardEvent } from 'react'
import { useState } from 'react'

const EXCLUDED_FORMATS = [
  ExportFormat.EXCEL_FLAT,
  ExportFormat.PDF_SALARY,
  ExportFormat.POWEROFFICE,
  ExportFormat.TRIPLETEX,
  ExportFormat.TWENTYFOUR_SEVEN_OFFICE,
  ExportFormat.VISMA,
]

export const ExportCompanyIssuesCard: React.FC = () => {
  const translations = useTranslate({
    exportAll: 'settings.export-all-issues-for-period',
    exportType: 'common.export-type',
    selectFromList: 'issues.select-recipient-from-list',
    selectedRecipients: 'issues.selected-recipients',
    addWithEmail: 'issues.add-recipient-with-email',
    errorEmail: 'error.valid-email-required',
    includeArchived: 'common.include-archived',
    add: 'common.add',
    remove: 'common.remove',

    exportTypes: {
      report: 'common.report',
      categoryStatistics: 'common.category-statistics',
      riskAspectStatistics: 'common.risk-aspect-statistics',
    },

    toasts: {
      emailAlreadySelected: 'common.email-already-selected',
    },
  })

  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const addToast = useToast()

  const me = useUser()
  const { data: usersData } = useQuery(USERS_COMBOBOX_QUERY, {
    variables: {
      includeSuperuser: me.isSuperuser,
      filterLeaderGroup: false,
      filterSalaryResponsible: false,
    },
  })

  const exportTypes: ComboboxItem<ExportType>[] = [
    { value: ExportType.ISSUES, label: translations.exportTypes.report },
    {
      value: ExportType.ISSUES_RISK_ASPECT_STATISTICS,
      label: translations.exportTypes.riskAspectStatistics,
    },
    {
      value: ExportType.ISSUES_CATEGORY_STATISTICS,
      label: translations.exportTypes.categoryStatistics,
    },
  ]

  const { loading, form, submit, updateForm, handleExport, handler } =
    useMassIssueExportForm()

  const recipients = form.recipients ?? []

  function handlePeriodChange(value: Partial<ExportPeriod>) {
    updateForm({
      ...value,
      ...(value.dateStart && { dateStart: startOfDay(value.dateStart) }),
      ...(value.dateEnd && { dateEnd: endOfDay(value.dateEnd) }),
    })
  }

  function handleUserSelect(userId: string) {
    const user = usersData?.allUsersList.find(user => user.id === userId)
    if (user && form.recipients && !form.recipients.includes(user.email)) {
      updateForm({ recipients: [...form.recipients, user.email] })
    } else {
      addToast('warning', translations.toasts.emailAlreadySelected)
    }
  }

  function handleChangeEmail(value: string) {
    if (!value) {
      setEmailError(null)
      setEmail('')
      return
    }
    setEmailError(emailValidation(value) ? null : translations.errorEmail)
    setEmail(value)
  }

  function handleAddEmail() {
    if (!form.recipients) return

    if (email && form.recipients.includes(email)) {
      addToast('warning', translations.toasts.emailAlreadySelected)
    }

    if (email && !emailError && !form.recipients.includes(email)) {
      updateForm({ recipients: [...form.recipients, email] })
      setEmail('')
    }
  }

  function handleEmailKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter') return
    event.preventDefault()
    handleAddEmail()
  }

  function handleDeleteEmail(email: string) {
    updateForm({ recipients: recipients.filter(value => value !== email) })
  }

  return (
    <ExportCard
      title={translations.exportAll}
      loading={loading}
      onSubmit={submit(handleExport)}
    >
      <ExportPeriodField value={form} onChange={handlePeriodChange} />

      <ExportMethodField
        value={form.exportOption}
        onChange={handler('exportOption')}
      />

      <ExportField label={translations.exportType}>
        <Combobox
          mode="single"
          noSearch
          value={form.exportType}
          items={exportTypes}
          onChange={value => {
            if (value) updateForm({ exportType: value })
          }}
        />
      </ExportField>

      <ExportFormatField
        value={form.exportFormat}
        exclude={EXCLUDED_FORMATS}
        onChange={handler('exportFormat')}
      />

      {form.exportOption === ExportOption.MAIL && (
        <>
          <ExportField label={translations.selectFromList}>
            <UserCombobox
              mode="single"
              value=""
              clearable={false}
              onChange={handleUserSelect}
            />
          </ExportField>

          <ExportField label={translations.addWithEmail} error={emailError}>
            <div className="flex items-stretch gap-2">
              <div className="min-w-0 flex-1">
                <Input
                  type="email"
                  value={email}
                  autoComplete="off"
                  aria-invalid={!!emailError}
                  className={emailError ? 'border-red-700' : undefined}
                  onChange={handleChangeEmail}
                  onKeyDown={handleEmailKeyDown}
                />
              </div>

              <Button
                type="button"
                variant="outline"
                size="icon-lg"
                className="h-auto shrink-0 rounded-xl"
                aria-label={translations.add}
                disabled={!email || !!emailError}
                onClick={handleAddEmail}
              >
                <Plus />
              </Button>
            </div>
          </ExportField>

          {recipients.length > 0 && (
            <ExportField label={translations.selectedRecipients} wide>
              <ul className="flex flex-wrap gap-2">
                {recipients.map(recipient => (
                  <li
                    key={recipient}
                    className="flex items-center gap-1.5 rounded-full border border-stone-200 bg-stone-50 py-1 pl-3 pr-1.5 text-sm text-stone-700"
                  >
                    <span className="truncate">{recipient}</span>
                    <button
                      type="button"
                      className="rounded-full p-0.5 text-stone-400 hover:bg-stone-200 hover:text-stone-900"
                      aria-label={translations.remove}
                      onClick={() => handleDeleteEmail(recipient)}
                    >
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            </ExportField>
          )}
        </>
      )}

      <label className="flex w-fit cursor-pointer items-center gap-3 text-sm font-medium text-stone-700 md:col-span-2">
        <Checkbox
          size="sm"
          value={form.includeArchived}
          onChange={checked => updateForm({ includeArchived: !!checked })}
        />
        {translations.includeArchived}
      </label>
    </ExportCard>
  )
}
